import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  TextField,
  Typography
} from '@mui/material';
import SignUP from 'assets/join-bg.png';
import ButtonCustom from 'components/ButtonCustom';
import PasswordTextField from 'components/PasswordTextField';
import UseAuthContext from 'context/AuthContext';
import UseLanguageContext from 'context/LanguageContext';
import { useSnackbar } from 'notistack';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { signupUsingInvitation } from 'services/invitation-signup';
import { getErrorHtmlContent } from 'utils/utils';

const SignUpByInvite = () => {
  const location: any = useLocation().state;
  const navigate = useNavigate();
  const { language, setLanguage } = useContext(UseLanguageContext);
  const [email, setEmail] = useState(location?.email);
  const [orgID, setOrgID] = useState(location?.organizationID);
  const [languageState, setLanguageState] = useState(language);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [terms, setTerms] = useState(false);
  const [receiveEmail, setReceiveEmail] = useState(true);
  const [isLoading, setisLoading] = useState(false);
  const { t } = useTranslation();
  const { loginAfterInvitationProcess } = useContext(UseAuthContext);
  const { enqueueSnackbar } = useSnackbar();

  const signUpHandler = async (e: any) => {
    try {
      e.preventDefault();
      setisLoading(true);
      const response = await signupUsingInvitation({
        email: email,
        password: password,
        phone: phone,
        name: name,
        allow_policy: terms,
        allow_mailing: receiveEmail,
        organization: orgID,
        language: languageState,
      });
      await loginAfterInvitationProcess(response.data);
      setisLoading(false);
    } catch (error: any) {
      if (error) {
        enqueueSnackbar(getErrorHtmlContent(error), { variant: 'error' });
      }
      setisLoading(false);
    }
  };

  useEffect(() => {
    if (location === null || !location.email || !location.organizationID) {
      navigate('/', {
        replace: true,
      });
    }
  }, []);

  useEffect(() => {
    if (location.language) {
      setLanguage(location.language);
    }
  }, []);

  useEffect(() => {
    setLanguageState(languageState);
  }, [language, languageState]);

  return (
    <Box
      sx={{
        width: "100%",
        display: 'flex',
        gap: { xs: "0px", md: "0px" },
        flexDirection: { xs: 'column', md: "row-reverse" },
        justifyContent: 'space-between',
        alignItems: "center",
        mt: "50px"
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: { xs: '100%', md: '60%' }
        }}
      >
        <Box component={"img"} sx={{ width: { xs: "210px", md: "400px" }, height: { xs: "210px", md: "400px" } }} src={SignUP} alt="Company" />
      </Box>

      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        width: { xs: "100%", md: "500px" }
      }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            mb: "30px",
            mt: { xs: "50px", md: "0px" }
          }}
        >
          <Typography variant='h1' >{t('SIGNUP_INVITE_HEADER')}</Typography>
        </Box>

        <Box component='form' style={{ width: '100%' }} className='tab-content-form'>
          <TextField
            fullWidth
            variant='standard'
            id='full_name'
            name='full_name'
            autoFocus
            label={t('INVITE_SIGNUP_NAME')}
            helperText={t('INVITE_SIGNUP_NAME_HELPER_TEXT')}
            inputProps={{
              autoComplete: 'new-password',
              form: {
                autoComplete: 'off',
              },
            }}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            variant='standard'
            fullWidth
            type='phone'
            name='phone'
            dir='ltr'
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            label={t('INVITE_SIGNUP_PHONE')}
          />
          <TextField
            variant='standard'
            fullWidth
            name='email'
            label={t('INVITE_SIGNUP_EMAIL')}
            helperText={t('INVITE_SIGNUP_EMAIL_HELPER_TEXT')}
            id='email'
            type='email'
            disabled={true}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <PasswordTextField
            name='password'
            sx={{ '& .MuiInputBase-formControl': { letterSpacing: "4px" } }}
            label={t('INVITE_SIGNUP_PASSWORD')}
            fullWidth
            helperText={t('INVITE_SIGNUP_PASSWORD_HELPER_TEXT')}
            dir="ltr"
            variant='standard'
            value={password}
            onChange={(e: any) => setPassword(e.target.value)}
            inputProps={{
              autoComplete: 'new-password',
              form: {
                autoComplete: 'off',
              },
            }}
          />

          <Box sx={{ gap: 0 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.2rem',
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    sx={{
                      fontSize: "16px"
                    }}
                    value={terms}
                    onChange={(event: any, checked: boolean) => {
                      setTerms(checked);
                    }}
                  />
                }
                sx={{
                  mr: '0rem',
                  fontSize: "16px"
                }}
                label={t('INVITE_SIGNUP_TERMS')}
              />
              <a
                style={{ fontSize: "16px" }}
                href='https://designbuilder.co.il/policy/'
                target='_blank'
                className='term_link'
              >
                {t('INVITE_SIGNUP_TERMS_LINK')}
              </a>
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={receiveEmail}
                  onChange={(event: any, checked: boolean) => {
                    setReceiveEmail(checked);
                  }}
                />
              }
              sx={{ fontSize: "16px !important" }}
              label={t('INVITE_SIGNUP_EMAIL_RECEIVE')}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <ButtonCustom
              color='primary'
              type='submit'
              variant='contained'
              onClick={signUpHandler}
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={20} color='inherit' /> : undefined}
            >
              {t('INVITE_SIGNUP_BUTTON')}
            </ButtonCustom>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SignUpByInvite;
